import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen, ArrowRight, CheckCircle, Play, Video,
  Brain, BarChart3, Trophy, Target, Gamepad2,
  Clock, Globe, Shield, Zap, ChevronDown,
  Users, School, GraduationCap, Sparkles
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
  InteractiveCard,
  MagneticButton,
  FloatingElement,
  GSAPCounter,
} from '@/components/ui/OptimizedAnimations';
import {
  CharacterStagger,
  SparklesCore,
  Spotlight,
} from '@/components/ui/AceternityAnimations';

const features = [
  {
    icon: Brain,
    title: 'AI-Powered Tutoring',
    description: 'Intelligent tutoring system that adapts to each student\'s learning pace and style. Personalized recommendations and instant doubt resolution.',
  },
  {
    icon: Video,
    title: 'Rich Video Library',
    description: 'Thousands of high-quality video lessons across subjects. Interactive transcripts, bookmarks, and note-taking built in.',
  },
  {
    icon: Target,
    title: 'Adaptive Assessments',
    description: 'Smart assessments that adjust difficulty based on performance. Detailed analytics identify knowledge gaps instantly.',
  },
  {
    icon: BarChart3,
    title: 'Progress Tracking',
    description: 'Real-time dashboards for students, teachers, and parents. Track learning outcomes and predict performance.',
  },
  {
    icon: Gamepad2,
    title: 'Gamification',
    description: 'Points, badges, leaderboards, and learning streaks. Make education engaging and habit-forming.',
  },
  {
    icon: Trophy,
    title: 'Competency Mapping',
    description: 'Map learning outcomes to skills and competencies. Generate skill certificates for career readiness.',
  },
];

const useCases = [
  {
    title: 'K-12 Schools',
    desc: 'Supplement classroom teaching with interactive content',
    icon: School,
  },
  {
    title: 'Universities',
    desc: 'Blended learning and flipped classroom models',
    icon: GraduationCap,
  },
  {
    title: 'Corporate Training',
    desc: 'Employee upskilling and compliance training',
    icon: Users,
  },
];

const stats = [
  { value: '10', suffix: 'M+', label: 'Lessons Completed' },
  { value: '95', suffix: '%', label: 'Completion Rate' },
  { value: '35', suffix: '%', label: 'Better Scores' },
  { value: '24', suffix: '/7', label: 'AI Support' },
];

const aiFeatures = [
  { name: 'Doubt Resolution', desc: 'Instant AI answers to student questions' },
  { name: 'Learning Paths', desc: 'Personalized curriculum for each student' },
  { name: 'Performance Prediction', desc: 'Early warning for at-risk students' },
  { name: 'Content Recommendations', desc: 'Smart suggestions based on progress' },
];

const faqs = [
  {
    question: 'How does the AI tutoring work?',
    answer: 'Our AI analyzes student interactions, assessment results, and learning patterns to provide personalized recommendations. It can answer questions, explain concepts in multiple ways, and adapt content difficulty in real-time.',
  },
  {
    question: 'Can we upload our own content?',
    answer: 'Yes! You can upload videos, documents, quizzes, and interactive content. Our system supports SCORM packages and integrates with popular content creation tools.',
  },
  {
    question: 'Does it work offline?',
    answer: 'Our mobile apps support offline mode for video lessons and assessments. Content syncs automatically when connectivity is restored.',
  },
  {
    question: 'How is student data protected?',
    answer: 'We\'re ISO 27001 certified with AES-256 encryption. Data is stored in region-specific servers to comply with local regulations. We never sell or share student data.',
  },
];

const FAQItem = ({ faq, isOpen, onToggle }) => (
  <motion.div className="glass-card overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full p-6 flex items-center justify-between text-left hover:bg-glass-hover transition-colors"
    >
      <span className="text-foreground font-medium pr-4">{faq.question}</span>
      <motion.div
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.3 }}
      >
        <ChevronDown className="w-5 h-5 text-orange flex-shrink-0" />
      </motion.div>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="px-6 pb-6 text-muted-foreground">
            {faq.answer}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const LMSPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="relative bg-void min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <Spotlight className="-top-40 left-0 md:left-60 md:-top-20" fill="hsl(16, 100%, 60%)" />

        <div className="absolute inset-0 z-[1]">
          <SparklesCore
            id="lmsSparkles"
            background="transparent"
            minSize={0.4}
            maxSize={1}
            particleDensity={30}
            particleColor="#FF6B35"
          />
        </div>

        <FloatingElement duration={8} distance={20} className="absolute top-20 right-10 opacity-10">
          <div className="w-64 h-64 border border-orange/30 rounded-full" />
        </FloatingElement>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <ScrollReveal delay={0.1}>
                <Link to="/products" className="inline-flex items-center gap-2 text-orange text-sm mb-6 hover:underline">
                  <ArrowRight className="w-4 h-4 rotate-180" />
                  Back to Products
                </Link>
              </ScrollReveal>

              <ScrollReveal delay={0.2}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-orange/20 flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-orange" />
                  </div>
                  <span className="px-3 py-1 rounded-full bg-orange/10 text-orange text-xs font-mono">
                    Institutions & Corporates
                  </span>
                </div>
              </ScrollReveal>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-bold text-foreground mb-4">
                <CharacterStagger
                  text="LMS"
                  delay={0.3}
                  staggerDelay={0.05}
                />
              </h1>
              <h2 className="text-2xl sm:text-3xl font-display text-gradient-orange mb-6">
                AI-Powered Learning Management
              </h2>

              <ScrollReveal delay={0.6}>
                <p className="text-muted-foreground text-lg mb-8 max-w-xl">
                  Transform learning outcomes with personalized AI tutoring, adaptive assessments,
                  and gamified experiences. Students learn 35% faster with better retention.
                </p>
              </ScrollReveal>

              <ScrollReveal delay={0.8}>
                <div className="flex flex-col sm:flex-row gap-4">
                  <MagneticButton strength={0.2}>
                    <Link to="/contact">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="bg-orange text-primary-foreground px-8 py-4 rounded-full font-medium flex items-center gap-2 shadow-orange-glow"
                      >
                        Book a Demo
                        <ArrowRight className="w-5 h-5" />
                      </motion.button>
                    </Link>
                  </MagneticButton>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-secondary px-8 py-4"
                  >
                    <Play className="w-5 h-5" />
                    See AI in Action
                  </motion.button>
                </div>
              </ScrollReveal>
            </div>

            {/* Stats Card */}
            <ScrollReveal direction="right" delay={0.4}>
              <div className="glass-card p-8 border-orange/20">
                <h3 className="text-display text-xl text-foreground mb-6">
                  Proven Learning Outcomes
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  {stats.map((stat, index) => (
                    <div key={stat.label} className="text-center">
                      <div className="text-3xl font-display font-bold text-orange">
                        <GSAPCounter value={stat.value} suffix={stat.suffix} duration={2} />
                      </div>
                      <div className="text-sm text-muted-foreground">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* AI Features Highlight */}
      <section className="py-24 bg-void-light/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-card p-8 md:p-12 border-orange/20 relative overflow-hidden">
            <div className="absolute top-4 right-4 px-4 py-2 rounded-full bg-orange/20 text-orange text-sm font-medium flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              AI-Powered
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <ScrollReveal>
                  <span className="font-mono text-xs text-orange tracking-widest uppercase mb-4 block">
                    Intelligent Learning
                  </span>
                  <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-6">
                    AI That Actually<br />
                    <span className="text-gradient-orange">Teaches</span>
                  </h2>
                  <p className="text-muted-foreground mb-8">
                    Our AI doesn&apos;t just deliver content — it understands how each student learns.
                    It adapts in real-time, identifies struggling concepts before they become problems,
                    and provides personalized support 24/7.
                  </p>
                  <ul className="space-y-3">
                    {[
                      'Understands natural language questions',
                      'Explains concepts in multiple ways',
                      'Provides worked examples',
                      'Tracks misconceptions over time',
                    ].map((item) => (
                      <li key={item} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-orange flex-shrink-0" />
                        <span className="text-foreground text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </ScrollReveal>
              </div>

              <ScrollReveal direction="right">
                <div className="grid grid-cols-2 gap-4">
                  {aiFeatures.map((feature, index) => (
                    <motion.div
                      key={feature.name}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                      whileHover={{ scale: 1.02, y: -4 }}
                      className="glass-card p-5 text-center"
                    >
                      <div className="w-10 h-10 rounded-xl bg-orange/20 flex items-center justify-center mx-auto mb-3">
                        <Brain className="w-5 h-5 text-orange" />
                      </div>
                      <h4 className="text-sm font-display font-semibold text-foreground mb-1">
                        {feature.name}
                      </h4>
                      <p className="text-xs text-muted-foreground">{feature.desc}</p>
                    </motion.div>
                  ))}
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-void">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <span className="font-mono text-xs text-orange tracking-widest uppercase mb-4 block">
              Complete Platform
            </span>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-4">
              Everything for Modern<br />
              <span className="text-gradient-orange">Digital Learning</span>
            </h2>
          </ScrollReveal>

          <StaggerContainer staggerDelay={0.1} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <StaggerItem key={feature.title}>
                <InteractiveCard hoverScale={1.02} hoverY={-6} glowColor="rgba(255,107,53,0.2)">
                  <div className="glass-card p-6 h-full border-orange/10 hover:border-orange/30 transition-colors">
                    <div className="w-12 h-12 rounded-xl bg-orange/20 flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-orange" />
                    </div>
                    <h3 className="text-display text-lg text-foreground mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                </InteractiveCard>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 bg-void-light/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <span className="font-mono text-xs text-orange tracking-widest uppercase mb-4 block">
              Flexible Deployment
            </span>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-4">
              Built for Every<br />
              <span className="text-gradient-orange">Learning Environment</span>
            </h2>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <ScrollReveal key={useCase.title} delay={index * 0.1}>
                <InteractiveCard hoverScale={1.02} hoverY={-6} glowColor="rgba(255,107,53,0.2)">
                  <div className="glass-card p-8 text-center h-full border-orange/10 hover:border-orange/30 transition-colors">
                    <div className="w-16 h-16 rounded-2xl bg-orange/20 flex items-center justify-center mx-auto mb-6">
                      <useCase.icon className="w-8 h-8 text-orange" />
                    </div>
                    <h3 className="text-display text-xl text-foreground mb-3">
                      {useCase.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {useCase.desc}
                    </p>
                  </div>
                </InteractiveCard>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className="py-12 bg-void border-y border-glass-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-8">
            {[
              { icon: Clock, label: 'Quick Setup' },
              { icon: Globe, label: 'Multi-language' },
              { icon: Shield, label: 'GDPR Compliant' },
              { icon: Zap, label: 'Offline Support' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange/20 flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-orange" />
                </div>
                <span className="text-foreground font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-void">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-12">
            <span className="font-mono text-xs text-orange tracking-widest uppercase mb-4 block">
              Common Questions
            </span>
            <h2 className="text-display text-3xl text-foreground">
              Frequently Asked <span className="text-gradient-orange">Questions</span>
            </h2>
          </ScrollReveal>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <ScrollReveal key={index} delay={index * 0.1}>
                <FAQItem
                  faq={faq}
                  isOpen={openFaq === index}
                  onToggle={() => setOpenFaq(openFaq === index ? null : index)}
                />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-void-light/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-6">
              Ready to Transform<br />
              <span className="text-gradient-orange">Learning Outcomes?</span>
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
              Join institutions worldwide that have improved student performance by 35%
              with our AI-powered LMS. See it in action with a personalized demo.
            </p>
            <MagneticButton strength={0.2}>
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-orange text-primary-foreground px-10 py-5 rounded-full font-medium text-lg flex items-center gap-2 mx-auto shadow-orange-glow"
                >
                  Book Your Free Demo
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </Link>
            </MagneticButton>
          </ScrollReveal>
        </div>
      </section>

      {/* Explore Suite Section */}
      <section className="py-16 bg-void border-t border-glass-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-8">
            <h3 className="text-display text-xl text-foreground">
              Explore Our <span className="text-gradient-teal">Suite</span>
            </h3>
          </ScrollReveal>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { name: 'School ERP', icon: School, href: '/products/school-erp', color: 'teal' },
              { name: 'College ERP', icon: GraduationCap, href: '/products/college-erp', color: 'orange' },
              { name: 'Lynkt', icon: Users, href: '/products/lynkt', color: 'teal' },
            ].map((product) => (
              <Link key={product.name} to={product.href}>
                <motion.div
                  whileHover={{ scale: 1.02, y: -4 }}
                  className={cn(
                    'glass-card p-6 flex items-center gap-4 cursor-pointer',
                    'border transition-colors duration-300',
                    product.color === 'teal' ? 'hover:border-teal/40' : 'hover:border-orange/40'
                  )}
                >
                  <div className={cn(
                    'w-12 h-12 rounded-xl flex items-center justify-center',
                    product.color === 'teal' ? 'bg-teal/20' : 'bg-orange/20'
                  )}>
                    <product.icon className={cn(
                      'w-6 h-6',
                      product.color === 'teal' ? 'text-teal' : 'text-orange'
                    )} />
                  </div>
                  <div>
                    <h4 className="text-display text-foreground">{product.name}</h4>
                    <p className="text-xs text-muted-foreground">Explore product</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground ml-auto" />
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LMSPage;
